import {css} from '@emotion/css';
import {useMemo} from 'react';

import {useBreakpoints} from '@br/mg-components';

import {transitionSpeed} from '../../config/vars';
import {useWolfTheme} from '../../contexts';

import {NAV_CLOSED_WIDTH, NAV_OPEN_WIDTH} from './SideMenu';

const useStyle = (sideMenuOpen: boolean, sideMenuPreview: boolean, controlledState?: boolean) => {
    const theme = useWolfTheme();
    const bp = useBreakpoints();

    const open = sideMenuOpen || sideMenuPreview || controlledState;

    return useMemo(
        () => ({
            containerStyle: css({
                height: '100%',
                display: 'grid',
                gridTemplateRows: '1fr',
            }),

            menuContainer: css({
                position: 'relative',
                transition: transitionSpeed,

                [bp.lg]: {
                    width: sideMenuOpen || controlledState ? NAV_OPEN_WIDTH : NAV_CLOSED_WIDTH,
                },
            }),

            menuContainerStyle: css({
                position: 'absolute',
                zIndex: theme.elevation.sideMenu,
                height: '100%',

                left: sideMenuOpen || controlledState ? '0' : `-${NAV_OPEN_WIDTH}`,

                // Overwrite veneer styles. visibiity hidden side menu group elements that are closed.
                // https://github.azc.ext.hp.com/veneer/veneer/issues/5566
                '& .vn-animated-height__container--exiting': {
                    visibility: 'hidden',
                },

                // Overwrite veneer side menu styles and remove annoying transitions.
                '& .vn-animated-height__container, & svg': {
                    transition: 'none !important',
                },

                '& nav:not(:first-of-type)': {
                    // Veneer bug, casues margin top to change based on if the side menu is open or closed.
                    // Overwriting to prevent jumping content.
                    // Veneer issue: https://github.azc.ext.hp.com/veneer/veneer/issues/5944
                    marginTop: `${theme.orbitalFour} !important`,
                    paddingTop: `${theme.orbitalThree} !important`,
                },

                [bp.lg]: {
                    left: 0,
                },
            }),

            menuInnerStyle: css({
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'stretch',
                height: '100%',
            }),

            footerStyles: css({
                opacity: open ? 1 : 0,
                visibility: open ? 'visible' : 'hidden',
                transition: `opacity ${transitionSpeed}`,
                pointerEvents: 'none',
                width: '100%',
                overflow: 'hidden',
            }),

            scrollBar: {
                height: '0',
                overflowX: 'hidden',
                overflowY: open ? 'auto' : 'hidden',
                flexGrow: 1,
                flexShrink: 1,
                marginBottom: theme.orbitalFour,

                // Temporary patch to avoid problems with moving content on the side menu when used on the controller
                // / with expanding collapsing menu items. Should be checked on the controller whenever modifying this stuff.
                '& .vn-side-menu__item-col-label': {
                    minWidth: 164,
                    maxWidth: 164,
                    // eslint-disable-next-line i18next/no-literal-string
                    display: 'block !important',
                    opacity: open ? 1 : 0,
                },
            },

            logo: css({
                display: 'flex',
                justifyContent: 'center',
                transition: transitionSpeed,
            }),

            toggle: css({
                display: 'none',
                position: 'absolute',
                bottom: 6,
                right: open ? 8 : '50%',
                transform: open ? 'rotate(-180deg)' : 'translateX(50%)',
                transition: transitionSpeed,

                [bp.lg]: {
                    display: 'block',
                },
            }),

            toggleWrapper: css({
                display: 'flex',
                justifyContent: 'flex-end',
                transition: transitionSpeed,
            }),
        }),
        [bp.lg, sideMenuOpen, controlledState, theme.elevation.sideMenu, theme.orbitalFour, theme.orbitalThree, open]
    );
};

export default useStyle;
